import { useUpdateEffect } from 'react-use';

import {
   ConfidentialityConfiguration,
   ConfidentialityConfigurationQuery,
   ConfidentialityConfigurationSetCommand,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { Translate, useFormController } from '@dreryk/react-common';
import {
   colorsMatrix,
   dispatchSnackbarPositiveEvent,
   RadioGroup,
   RadioGroupItem,
   StrongText,
   Text,
} from '@dreryk/react-components';

import { generalSettingsTranslationKeys } from '../../../translations';

import { StyledPrescriptionIssuingAccessField } from './PrescriptionIssuingAccessField.styled';

const t = generalSettingsTranslationKeys.confidentialitySettings.prescriptionIssuingAccess;

type PrescriptionIssuingAccessRadioId = 'yes' | 'no';

const PRESCRIPTION_ISSUING_ACCESS_RADIO_ITEMS: RadioGroupItem<PrescriptionIssuingAccessRadioId>[] = [
   {
      id: 'yes',
      label: t.options.yes,
   },
   {
      id: 'no',
      label: t.options.no,
   },
];

const toRadioId = (allowed: boolean): PrescriptionIssuingAccessRadioId => (allowed ? 'yes' : 'no');

type PrescriptionIssuingAccessFieldFormBean = {
   prescriptionIssuingAccess: PrescriptionIssuingAccessRadioId;
};

const useConfidentialityPrescriptionIssuingAccessSetCommand = (
   confidentialityConfiguration: ConfidentialityConfiguration
): UseCqrsCommandResult<void, PrescriptionIssuingAccessFieldFormBean> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new ConfidentialityConfigurationSetCommand();
         command.prescriptionIssuingAccessAllowed = parameters.prescriptionIssuingAccess === 'yes';
         command.monitoredAccessDuration = confidentialityConfiguration.monitoredAccessDuration;
         command.authorizedAccessDuration = confidentialityConfiguration.authorizedAccessDuration;
         return command;
      },
      {
         onSuccess: async (ignore, parameters) => {
            await refetchCqrsQuery(ConfidentialityConfigurationQuery);
            dispatchSnackbarPositiveEvent(parameters.prescriptionIssuingAccess === 'yes' ? t.saveOn : t.saveOff);
         },
      }
   );
};

type PrescriptionIssuingAccessFieldProps = {
   readonly confidentialityConfiguration: ConfidentialityConfiguration;
};

export const PrescriptionIssuingAccessField = (props: PrescriptionIssuingAccessFieldProps): JSX.Element => {
   const initialValue = toRadioId(props.confidentialityConfiguration.prescriptionIssuingAccessAllowed);

   const { register, watch } = useFormController<PrescriptionIssuingAccessFieldFormBean>({
      prescriptionIssuingAccess: initialValue,
   });

   const {
      executeAsync: setPrescriptionIssuingAccess,
      loading,
   } = useConfidentialityPrescriptionIssuingAccessSetCommand(props.confidentialityConfiguration);

   const currentValue = watch('prescriptionIssuingAccess');

   useUpdateEffect(() => {
      setPrescriptionIssuingAccess({ prescriptionIssuingAccess: currentValue });
   }, [currentValue]);

   return (
      <StyledPrescriptionIssuingAccessField>
         <StrongText testId={'prescriptionIssuingAccessLabel'}>
            <Translate>{t.label}</Translate>
         </StrongText>
         <Text testId={'prescriptionIssuingAccessDescription'} color={colorsMatrix.grey.dark}>
            <Translate>{t.description}</Translate>
         </Text>
         <RadioGroup<PrescriptionIssuingAccessRadioId>
            testId={'prescriptionIssuingAccess'}
            items={PRESCRIPTION_ISSUING_ACCESS_RADIO_ITEMS}
            controller={register('prescriptionIssuingAccess')}
            defaultValue={initialValue}
            disabled={loading}
            horizontal
         />
      </StyledPrescriptionIssuingAccessField>
   );
};
